import React, { useEffect } from "react";

import { useGetStorewiseLeads } from "@/api/lead/get-storewise-leads";
import { Card } from "@/components/ui/card";
import PaginationCombo from "@/components/ui/pagination-combo";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSearchParams } from "react-router";
import NoDataFound from "@/components/layout/no-data-found";
import { Skeleton } from "@/components/ui/skeleton";
import useGlobalPermission from "@/store/user-permissions";
import { cn } from "@/lib/utils";

type Props = {
    setTotalLeads: (value: number) => void;
};

const LeadsTable: React.FC<Props> = ({ setTotalLeads }) => {
    const [searchParams] = useSearchParams();
    const {
        data: leads,
        isLoading: leadsLoading,
        isError: leadsError,
    } = useGetStorewiseLeads({
        skip: (parseInt(searchParams.get("page") || "1") - 1) * parseInt(searchParams.get("per_page") || "10"),
        limit: parseInt(searchParams.get("per_page") || "10"),
        search: searchParams.get("search") || null,
    });

    useEffect(() => {
        setTotalLeads(leads?.pagination?.total_entity || 0);
    }, [leads, setTotalLeads]);

    const { permissionList } = useGlobalPermission();
    const leadsPermission = permissionList.find((item) => item.module.name === "Leads");

    return (
        <Card className="overflow-hidden">
            <div className="max-h-[calc(100vh-200px)] overflow-y-scroll">
                <Table>
                    <TableHeader className="sticky top-0 border-b bg-blue-100  shadow-sm">
                        <TableRow>
                            <TableHead className="w-[100px] p-4 font-bold text-blue-500">Email</TableHead>
                            <TableHead className="p-4 font-bold text-blue-500">Phone No.</TableHead>
                            <TableHead className="p-4 font-bold text-blue-500">Name</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {leads?.data?.map((row: { email: string; phone: string; name: string }, index: number) => (
                            <TableRow key={index}>
                                <TableCell className={cn("p-4 font-medium")}>
                                    <p className={cn(!leadsPermission?.can_get_list && "blur-sm")}>
                                        {!leadsPermission?.can_get_list ? "No permission to view" : row.email}
                                    </p>
                                </TableCell>
                                <TableCell className={cn("p-4", !leadsPermission?.can_get_list && "blur-sm")}>
                                    {!leadsPermission?.can_get_list ? "No permission to view" : row.phone || "N/A"}
                                </TableCell>
                                <TableCell className={cn("p-4", !leadsPermission?.can_get_list && "blur-sm")}>
                                    {!leadsPermission?.can_get_list ? "No permission to view" : row.name || "N/A"}
                                </TableCell>
                            </TableRow>
                        ))}
                        {leadsLoading &&
                            [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
                                <TableRow key={item}>
                                    <TableCell className="p-4 font-medium">
                                        <Skeleton className="h-4 w-full" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-full" />
                                    </TableCell>
                                    <TableCell className="p-4">
                                        <Skeleton className="h-4 w-full" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        {(leadsError || !leads?.data) && !leadsLoading && (
                            <TableRow>
                                <TableCell colSpan={3} className="w-full text-center">
                                    <NoDataFound message="No Leads Found" />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {!leadsLoading && leads?.pagination?.total_entity > 0 && <PaginationCombo totalItems={leads?.pagination?.total_entity} />}
        </Card>
    );
};

export default LeadsTable;
